import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Container from "../components/Container";
import emailjs from "@emailjs/browser";
import { Helmet } from "react-helmet";
import { FaGamepad, FaLaptopCode, FaBriefcase, FaCheckCircle, FaUserTie, FaClock, FaGem, FaLifeRing } from "react-icons/fa";

export default function ContactUs() {

    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
        setTimeout(() => {
            const element = document.querySelector(location.hash);
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }, 100);
        }
    }, [location]);

    const currentUrl = typeof window !== "undefined" ? window.location.href : "https://www.credibletech.in/contactus";
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: ""
    });
    const [sending, setSending] = useState(false);
    const [popup, setPopup] = useState("");

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSending(true);
        setPopup("");

        emailjs
        .send(
            "service_ymk6pq9",
            "ct_apsa6ec",
            {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                service: formData.service,
                message: formData.message
            },
            "H8qppH7v7nvzF5PTr"
        )
        .then(
            (response) => {
                console.log("EmailJS response:", response);
                setPopup("success");

                // Reset form after 3 seconds
                setTimeout(() => {
                    setPopup("");
                    setFormData({
                    name: "",
                    email: "",
                    phone: "",
                    service: "",
                    message: ""
                    });
                    setSending(false);
                }, 3000);
            },
            (err) => {
                console.error("EmailJS error:", err);
                setPopup("error");
                setSending(false);
            }
        );
    };

    return (
        <>
            <Helmet>
                {/* Basic Meta */}
                <title>Contact Us | Credible Technologies - Refurbished Laptops & Laptop Repair in Bangalore</title>
                <meta
                    name="description"
                    content="Contact Credible Technologies for expert consultation, premium refurbished laptops, and custom workstation builds in Bangalore. Call, email, or visit us today!"
                />
                <meta
                    name="keywords"
                    content="contact, credible technologies, refurbished laptops, laptop repair, custom builds, Bangalore, expert consultation, business solutions, buy refurbished laptops, sell old laptops, business laptops, service center, student laptops"
                />
                <meta name="robots" content="index, follow" />
                
                {/* Dynamic URL */}
                <link rel="canonical" href={currentUrl} />
                <meta property="og:url" content={currentUrl} />

                {/* Open Graph */}
                <meta property="og:title" content="Contact Us | Credible Technologies" />
                <meta
                    property="og:description"
                    content="Contact Credible Technologies for expert consultation, premium refurbished laptops, and custom workstation builds in Bangalore."
                />
                <meta property="og:type" content="website" />
                <meta
                    property="og:image"
                    content="https://www.credibletech.in/assets/newlogo1.png"
                />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Contact Us | Credible Technologies" />
                <meta
                    name="twitter:description"
                    content="Contact Credible Technologies for expert consultation, premium refurbished laptops, and custom workstation builds in Bangalore."
                />
                <meta
                    name="twitter:image"
                    content="https://www.credibletech.in/assets/newlogo1.png"
                />

                {/* Fonts Preconnect */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap"
                    rel="stylesheet"
                />

                {/* Structured Data */}
                <script type="application/ld+json">{`{
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Credible Technologies",
                    "image": "https://www.credibletech.in/assets/newlogo1.png",
                    "url": "${currentUrl}",
                    "telephone": "+91-9538577199",
                    "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "1093, 26th B Main Rd, opposite Trends Woman, Putlanpalya, Jayanagara 9th Block, Jayanagar",
                    "addressLocality": "Bengaluru",
                    "addressRegion": "Karnataka",
                    "postalCode": "560069",
                    "addressCountry": "IN"
                    },
                    "description": "Contact Credible Technologies for expert consultation, premium refurbished laptops, and custom workstation builds in Bangalore.",
                    "openingHours": "Mo-Fr 09:00-18:00, Sa 10:00-16:00",
                    "contactPoint": {
                    "@type": "ContactPoint",
                    "telephone": "+91-9538577199",
                    "contactType": "customer service",
                    "areaServed": "IN",
                    "availableLanguage": "English"
                    },
                    "sameAs": [
                    "https://www.facebook.com/credibletech",
                    "https://www.instagram.com/credibletech",
                    "https://www.linkedin.com/company/credibletech"
                    ],
                    "hasMap": "https://goo.gl/maps/your-location"
                }`}</script>
                <script type="application/ld+json">{`
                    {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.credibletech.in/"
                        },
                        {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Contact Us",
                        "item": "https://www.credibletech.in/contactus"
                        }
                    ]
                    }
                `}</script>
            </Helmet>

            {/* Hero + Stats Combined Section */}
            <section className="relative py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white">
                <Container>
                    <div className="max-w-4xl mx-auto text-center px-4 sm:px-0">
                        <p className="uppercase tracking-widest text-blue-400 mb-3">
                            Let's Build Your
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                            Dream Machine
                        </h1>
                        <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-12">
                            Connect with Imthiaz and our expert team at{" "}
                            <span className="text-white font-semibold">Credible Technologies</span>. 
                            Get personalized consultation for your animation, gaming, or business workstation needs.
                        </p>

                        <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition transform hover:scale-105">
                                <p className="text-3xl font-bold text-blue-400">10,000+</p>
                                <p className="text-gray-300 mt-2">Custom Builds</p>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition transform hover:scale-105">
                                <p className="text-3xl font-bold text-blue-400">24+</p>
                                <p className="text-gray-300 mt-2">Years Experience</p>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition transform hover:scale-105">
                                <p className="text-3xl font-bold text-blue-400">99%</p>
                                <p className="text-gray-300 mt-2">Satisfaction</p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Contact Cards Section */}
            <section className="py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white">
                <Container>
                    <div className="text-center mb-16 max-w-3xl mx-auto px-4 sm:px-0">
                        <h2 className="text-3xl md:text-4xl font-bold">Get In Touch</h2>
                        <p className="mt-4 text-lg text-gray-300">
                            Multiple ways to connect with our experts
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-lg p-6 sm:p-8 hover:shadow-2xl transition transform hover:scale-105 text-center">
                            <div className="text-4xl text-blue-400 mb-4">📞</div>
                            <h3 className="text-xl font-semibold mb-2">Call Directly</h3>
                            <p className="mb-2 text-sm sm:text-base">+91 9538577199</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">Immediate consultation with Imthiaz</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">Available 9 AM - 8 PM</p>
                            <a href="tel:+919538577199" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition text-sm sm:text-base">
                                Call Now
                            </a>
                        </div>

                        <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-lg p-6 sm:p-8 hover:shadow-2xl transition transform hover:scale-105 text-center">
                            <div className="text-4xl text-blue-400 mb-4">✉️</div>
                            <h3 className="text-xl font-semibold mb-2">Email Expert</h3>
                            <p className="mb-2 text-sm sm:text-base">imthiaz@credibletech.in</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">Detailed project discussions & quotes</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">Response within 2 hours</p>
                            <a href="mailto:imthiaz@credibletech.in" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition text-sm sm:text-base">
                                Send Email
                            </a>
                        </div>

                        <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-lg p-6 sm:p-8 hover:shadow-2xl transition transform hover:scale-105 text-center">
                            <div className="text-4xl text-blue-400 mb-4">📍</div>
                            <h3 className="text-xl font-semibold mb-2">Visit Our Lab</h3>
                            <p className="mb-2 text-sm sm:text-base">Bangalore, Karnataka</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">See our premium builds in action</p>
                            <p className="text-gray-300 mb-4 text-sm sm:text-base">By appointment only</p>
                            <div className="w-full max-w-6xl mx-auto">
                                {/* <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.8549725272173!2d77.59154757507552!3d12.9170412873933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae156e82b1564b%3A0x5b6e967346b495b3!2sCredible%20Technologies%E2%80%93%20Buy%20%7C%20Sell%20%7C%20Refurbished%20%26%20Gaming%20Laptops%20in%20jayanagar%209th%20block!5e0!3m2!1sen!2sin!4v1767027195847!5m2!1sen!2sin"
                                    width="100%"
                                    height="450"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    className="rounded-xl shadow-lg"
                                /> */}

                                <a
                                    href="https://maps.app.goo.gl/s19uKCD9q6SkHqRz9"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition text-sm sm:text-base"
                                >
                                    Get Directions
                                </a>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Project Section with Form */}
            <section id="form" className="relative py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white overflow-hidden">
                <Container>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start px-4 sm:px-0">

                        {/* Left Cards */}
                        <div className="space-y-8">
                            <h2 className="text-4xl md:text-5xl font-bold text-white">Start Your Project</h2>
                            <p className="text-gray-300 text-lg">
                                Tell us about your requirements and we'll craft the perfect solution
                            </p>

                            <div className="grid gap-6 mt-6">
                                <div className="flex items-start gap-4 bg-white/10 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1">
                                    <FaGamepad className="text-blue-500 text-3xl mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">Gaming Powerhouses</h3>
                                        <p className="text-gray-300 text-sm">Latest GPU, RTX 4090, 240Hz displays, liquid cooling</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white/10 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1">
                                    <FaLaptopCode className="text-purple-500 text-3xl mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">Animation Workstations</h3>
                                        <p className="text-gray-300 text-sm">Maya, 3ds Max, rendering optimization</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white/10 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1">
                                    <FaBriefcase className="text-emerald-500 text-3xl mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">Business Solutions</h3>
                                        <p className="text-gray-300 text-sm">Enterprise security, productivity focus</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white/10 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition transform hover:-translate-y-1">
                                    <FaCheckCircle className="text-orange-500 text-3xl mt-1" />
                                    <div>
                                        <h3 className="font-semibold text-white text-lg">Expert Consultation & Lifetime Support</h3>
                                        <p className="text-gray-300 text-sm">Premium quality solutions tailored to you</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Form */}
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-10 shadow-xl relative">
                            <h3 className="text-2xl font-bold text-white mb-4">Let's Discuss Your Project</h3>
                            <p className="text-gray-300 mb-8">Every detail matters for your perfect build</p>

                            {popup && (
                                <div className={`absolute top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white ${popup === "success" ? "bg-green-500" : "bg-red-500"} font-semibold`}>
                                    {popup === "success" ? "Enquiry sent successfully!" : "Failed to send. Try again."}
                                </div>
                            )}

                            <form className="space-y-4" onSubmit={handleSubmit}>
                                <div>
                                    <label className="block text-gray-200 font-semibold mb-1">Your Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your full name"
                                        className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-200 font-semibold mb-1">Email Address *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="your.email@example.com"
                                        className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-200 font-semibold mb-1">Phone Number *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="+91 XXXXX XXXXX"
                                        className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-200 font-semibold mb-1">Service Required</label>
                                    <div className="relative">
                                        <select
                                        name="service"
                                        value={formData.service}
                                        onChange={handleInputChange}
                                        className="appearance-none w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                        <option value="" className="bg-black text-white">Select Service</option>
                                        <option value="Gaming PC" className="bg-black text-white">Gaming PC</option>
                                        <option value="Animation Workstation" className="bg-black text-white">Animation Workstation</option>
                                        <option value="Business Solution" className="bg-black text-white">Business Solution</option>
                                        </select>
                                        {/* Custom arrow */}
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                        <svg
                                            className="h-5 w-5 text-gray-300"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06-.02L10 10.585l3.71-3.396a.75.75 0 111.02 1.096l-4.25 3.889a.75.75 0 01-1.02 0l-4.25-3.889a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                                        </svg>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                <label className="block text-gray-200 font-semibold mb-1">Project Requirements *</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Describe your workflow, software requirements, performance expectations, and budget considerations..."
                                    className="w-full px-4 py-2 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                                    required
                                ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className={`w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition ${sending ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                    {sending ? "Sending..." : "Get Started Now"}
                                </button>
                                <p className="mt-2 text-gray-400 text-xs">Your information is secure and will only be used for project consultation.</p>
                            </form>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Final Highlights */}
            <section className="py-20 bg-gradient-to-br from-black via-slate-900 to-black text-white">
                <Container>
                    <div className="max-w-6xl mx-auto text-center mb-12 px-4 sm:px-0">
                        <h2 className="text-3xl md:text-4xl font-bold">Why Choose Us</h2>
                        <p className="text-gray-300 mt-3 max-w-2xl mx-auto">
                            Our commitment to quality, speed, and support ensures your project is handled professionally from start to finish.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {
                            [
                                { icon: <FaUserTie className="text-blue-500 text-4xl mb-4 mx-auto" />, title: "Expert Consultation", desc: "Personal guidance from Imthiaz with 25+ years experience" },
                                { icon: <FaClock className="text-purple-500 text-4xl mb-4 mx-auto" />, title: "Quick Response", desc: "Get initial consultation within 2 hours of contact" },
                                { icon: <FaGem className="text-emerald-500 text-4xl mb-4 mx-auto" />, title: "Premium Quality", desc: "Only the finest components and professional assembly" },
                                { icon: <FaLifeRing className="text-orange-500 text-4xl mb-4 mx-auto" />, title: "Lifetime Support", desc: "Ongoing technical support and upgrade consultations" }
                            ].map((item, idx) => (
                                <div key={idx} className="bg-white/10 p-6 sm:p-8 rounded-3xl shadow-xl transition-all duration-500 ease-in-out hover:shadow-2xl hover:scale-105">
                                    {item.icon}
                                    <h3 className="font-semibold text-center text-lg mb-2">{item.title}</h3>
                                    <p className="text-gray-300 text-center text-sm">{item.desc}</p>
                                </div>
                            ))
                        }
                    </div>
                </Container>
            </section>
        </>
    );
}
